import { parseEther } from "viem";

export function normalizeLowerAddress(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

export function formatTinyAmount(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n >= 1) return n.toFixed(6).replace(/\.?0+$/, "");
  if (n >= 0.000001) return n.toFixed(8).replace(/\.?0+$/, "");
  return n.toFixed(12).replace(/\.?0+$/, "");
}

function toWei(value) {
  try {
    return parseEther(String(value ?? "0").trim() || "0");
  } catch {
    return 0n;
  }
}

export function resolveBidBaseWei(row) {
  const lastWei = toWei(row?.highest_bid ?? 0);
  const startWei = toWei(row?.start_price ?? 0);
  return lastWei > 0n ? lastWei : startWei;
}

export function computeBidStepWei(baseWei) {
  if (baseWei <= 0n) return 1n;
  return baseWei / 10n > 0n ? baseWei / 10n : 1n;
}

export function computeNextBidFromBaseWei(baseWei) {
  if (baseWei <= 0n) return 0n;
  return (baseWei * 11n + 9n) / 10n;
}

export function computeNextBidWei(row) {
  return computeNextBidFromBaseWei(resolveBidBaseWei(row));
}

export function isNotEnoughPaymentError(error) {
  const text = String(
    error?.shortMessage || error?.message || error || "",
  ).toLowerCase();
  return text.includes("not_enough_payment");
}

export function parseToSec(value) {
  if (value == null || value === "") return 0;

  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : 0;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 1e12 ? Math.floor(value / 1000) : Math.floor(value);
  }

  const text = String(value).trim();
  if (!text) return 0;

  if (/^\d+$/.test(text)) {
    const num = Number(text);
    if (!Number.isFinite(num)) return 0;
    return num > 1e12 ? Math.floor(num / 1000) : Math.floor(num);
  }

  const mysqlLike = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
  const normalized = mysqlLike.test(text) ? `${text.replace(" ", "T")}Z` : text;
  const asMs = Date.parse(normalized);
  return Number.isFinite(asMs) ? Math.floor(asMs / 1000) : 0;
}

export function formatCountdownSec(totalSec) {
  const safe = Math.max(0, Math.floor(Number(totalSec) || 0));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function shortHash(value = "") {
  const text = String(value || "").trim();
  if (!text) return "-";
  if (text.length <= 18) return text;
  return `${text.slice(0, 10)}...${text.slice(-6)}`;
}

export function buildBscTxUrl(txHash = "") {
  const hash = String(txHash || "").trim();
  if (!hash) return "";
  return `https://bscscan.com/tx/${hash}`;
}

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

export function resolveEventType(row = {}) {
  return normalizeText(row?.event || "");
}

export function resolveBidder(row = {}) {
  return String(row?.highest_bidder ?? "").trim();
}

export function resolveRecipient(row = {}) {
  return String(row?.recipient ?? "").trim();
}

export function resolveAmountValue(row = {}) {
  return row?.amount ?? 0;
}

export function resolveCreatedAtValue(row = {}) {
  return row?.created_at ?? "";
}

function normalizeAmountKey(value) {
  const text = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!text) return "";
  if (!/^-?(?:\d+(?:\.\d+)?|\.\d+)(?:e[+-]?\d+)?$/.test(text)) return text;

  const negative = text.startsWith("-");
  const unsigned = negative ? text.slice(1) : text;
  const [mantissaRaw = "0", expRaw = "0"] = unsigned.split("e");
  const exponent = Number(expRaw);
  if (!Number.isFinite(exponent) || !Number.isInteger(exponent)) return text;

  let [intPart = "0", fracPart = ""] = mantissaRaw.split(".");
  intPart = intPart || "0";
  fracPart = fracPart || "";

  const digits = `${intPart}${fracPart}`.replace(/^0+(?=\d)/, "") || "0";
  const decimalPos = intPart.length + exponent;

  let expanded = "";
  if (decimalPos <= 0) {
    expanded = `0.${"0".repeat(Math.abs(decimalPos))}${digits}`;
  } else if (decimalPos >= digits.length) {
    expanded = `${digits}${"0".repeat(decimalPos - digits.length)}`;
  } else {
    expanded = `${digits.slice(0, decimalPos)}.${digits.slice(decimalPos)}`;
  }

  let [outInt = "0", outFrac = ""] = expanded.split(".");
  outInt = outInt.replace(/^0+(?=\d)/, "") || "0";
  outFrac = outFrac.replace(/0+$/, "");

  const normalized = outFrac ? `${outInt}.${outFrac}` : outInt;
  if (!negative) return normalized;
  return normalized === "0" ? "0" : `-${normalized}`;
}

function normalizeDisplayAmountKey(value) {
  const numeric = Number(value ?? 0);
  if (!Number.isFinite(numeric)) return "";
  return formatTinyAmount(numeric);
}

export function resolveTxHash(row = {}) {
  return normalizeText(row?.tx_hash || "");
}

export function dedupeRows(rows = [], keyBuilder) {
  const list = Array.isArray(rows) ? rows : [];
  const seen = new Set();
  const out = [];
  for (const row of list) {
    const key = String(keyBuilder?.(row) || "").trim();
    if (!key) {
      out.push(row);
      continue;
    }
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

export function buildBidEventKey(row = {}) {
  const txHash = resolveTxHash(row);
  if (txHash) return `bid:${txHash}`;

  const bidder = normalizeText(resolveBidder(row));
  const amount = normalizeAmountKey(resolveAmountValue(row));

  const blockNumber = String(row?.block_number ?? "").trim();
  const createdAt = String(resolveCreatedAtValue(row)).trim();
  return `bid:fallback:${blockNumber}:${createdAt}:${bidder}:${amount}`;
}

export function buildRebateKey(row = {}) {
  const txHash = resolveTxHash(row);
  const logIndex = String(row?.log_index ?? "").trim();
  if (txHash && logIndex) return `rebate:${txHash}:log:${logIndex}`;

  const recipient = normalizeText(resolveRecipient(row));
  const amount = normalizeAmountKey(resolveAmountValue(row));
  if (txHash) return `rebate:${txHash}:${recipient}:${amount}`;

  const blockNumber = String(row?.block_number ?? "").trim();
  const createdAt = String(resolveCreatedAtValue(row)).trim();
  return `rebate:fallback:${blockNumber}:${createdAt}:${recipient}:${amount}`;
}

export function buildRebateDisplayKey(row = {}) {
  const txHash = resolveTxHash(row);
  const recipient = normalizeText(resolveRecipient(row));
  const amountDisplay = normalizeDisplayAmountKey(resolveAmountValue(row));
  if (txHash && recipient && amountDisplay) {
    return `rebate:display:${txHash}:${recipient}:${amountDisplay}`;
  }
  return buildRebateKey(row);
}

export function buildAuctionEventKey(row = {}) {
  const eventType = resolveEventType(row);
  if (eventType === "bid") return buildBidEventKey(row);
  if (eventType === "rebate") return buildRebateKey(row);
  const txHash = resolveTxHash(row);
  const id = String(
    row?.id ??
      row?.log_index ??
      row?.block_number ??
      "",
  ).trim();
  return `event:${eventType}:${txHash}:${id}`;
}
